import React from "react";
import { NextSeo } from "next-seo";
import Head from "next/head";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { SITE_URL } from "@/config/sitemapUrls";
import { contentPath } from "@/content";
import type { ContentLink, ContentSection } from "@/content/types";
import type { ContentCard } from "@/utils/contentProps";
import { jsonLd } from "@/utils/jsonLd";
import { ContentCardLink, SECTION_LABEL } from "./ContentArticle";

// /blog and /compare: every page in the section, newest first, the newest
// one featured.

interface Props {
  section: ContentSection;
  title: string;
  metaDescription: string;
  h1: string;
  intro: string;
  pages: ContentCard[];
  // Older pages of the same kind that live outside the section, e.g. the
  // Weights alternative hub on /compare.
  extra?: (ContentLink & { description: string })[];
}

const ContentIndex = ({
  section,
  title,
  metaDescription,
  h1,
  intro,
  pages,
  extra = [],
}: Props) => {
  const canonical = `${SITE_URL}/${section}`;
  const [featured, ...rest] = pages;
  const collectionLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: h1,
    description: metaDescription,
    url: canonical,
    hasPart: [
      ...pages.map((p) => ({ label: p.h1, href: contentPath(p) })),
      ...extra,
    ].map((c) => ({
      "@type": "Article",
      headline: c.label,
      url: `${SITE_URL}${c.href}`,
    })),
  };

  return (
    <>
      <NextSeo
        title={title}
        description={metaDescription}
        canonical={canonical}
        openGraph={{
          title,
          description: metaDescription,
          url: canonical,
          type: "website",
          locale: "en_US",
          siteName: "Jammable",
        }}
      />
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: jsonLd(collectionLd) }}
        />
      </Head>
      <main className="tw-mx-auto tw-flex tw-w-full tw-max-w-[1280px] tw-flex-col tw-gap-8 tw-bg-white tw-px-4 tw-pb-16 tw-pt-2 tw-text-jam-ink md:tw-gap-10 md:tw-px-8 md:tw-pt-4">
        <section className="tw-relative tw-flex tw-flex-col tw-gap-5 tw-overflow-hidden tw-rounded-[34px] tw-bg-jam-ink tw-p-6 tw-pt-8 tw-text-white md:tw-rounded-[44px] md:tw-p-12">
          <span
            aria-hidden="true"
            className="tw-absolute -tw-right-16 -tw-top-24 tw-h-72 tw-w-72 tw-rounded-full tw-bg-jam-purple tw-opacity-70 tw-blur-3xl"
          />
          <span
            aria-hidden="true"
            className="tw-absolute -tw-bottom-24 tw-right-1/3 tw-h-48 tw-w-48 tw-rounded-full tw-bg-jam-lime tw-opacity-25 tw-blur-3xl"
          />
          <nav
            aria-label="Sections"
            className="tw-relative tw-flex tw-gap-2 tw-text-sm tw-font-extrabold"
          >
            {(["blog", "compare"] as const).map((s) => (
              <Link
                key={s}
                href={`/${s}`}
                aria-current={s === section ? "page" : undefined}
                className={`tw-rounded-full tw-px-4 tw-py-2 tw-no-underline ${
                  s === section
                    ? "tw-bg-jam-lime tw-text-jam-ink"
                    : "tw-bg-white/10 tw-text-white hover:tw-bg-white/20"
                }`}
              >
                {SECTION_LABEL[s]}
              </Link>
            ))}
          </nav>
          <h1 className="jam-display tw-relative tw-m-0 tw-max-w-3xl tw-text-[42px] tw-font-extrabold tw-leading-[1] tw-tracking-[-0.01em] md:tw-text-[72px]">
            {h1}
          </h1>
          <p className="tw-relative tw-m-0 tw-max-w-2xl tw-text-[15px] tw-font-semibold tw-leading-relaxed tw-text-white/75 md:tw-text-lg">
            {intro}
          </p>
        </section>

        {featured && <ContentCardLink card={featured} featured />}

        {(rest.length > 0 || extra.length > 0) && (
          <ul className="tw-m-0 tw-grid tw-list-none tw-grid-cols-1 tw-gap-4 tw-p-0 md:tw-grid-cols-2 lg:tw-grid-cols-3">
            {rest.map((c) => (
              <li key={c.slug}>
                <ContentCardLink card={c} />
              </li>
            ))}
            {extra.map((e) => (
              <li key={e.href}>
                <Link
                  href={e.href}
                  className="tw-group tw-flex tw-h-full tw-flex-col tw-gap-3 tw-rounded-[28px] tw-bg-jam-lav tw-p-6 tw-text-jam-ink tw-no-underline tw-transition hover:-tw-translate-y-0.5 hover:tw-shadow-[6px_6px_0_#D1FE17]"
                >
                  <span className="tw-text-xs tw-font-extrabold tw-uppercase tw-tracking-[0.12em] tw-text-jam-purple">
                    Guide
                  </span>
                  <h3 className="jam-display tw-m-0 tw-text-xl tw-font-extrabold tw-leading-tight">
                    {e.label}
                  </h3>
                  <p className="tw-m-0 tw-flex-1 tw-text-sm tw-font-semibold tw-leading-relaxed tw-text-jam-muted">
                    {e.description}
                  </p>
                  <span className="tw-flex tw-h-9 tw-w-9 tw-items-center tw-justify-center tw-self-end tw-rounded-full tw-bg-white tw-text-jam-purple tw-transition-colors group-hover:tw-bg-jam-lime group-hover:tw-text-jam-ink">
                    <ArrowRight size={17} strokeWidth={2.6} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </>
  );
};

export default ContentIndex;
